import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { AdminLayout } from '@/app/shell/admin-layout';
import {
  type AdminFlag,
  type CreateFlagInput,
  useAddTeamOverride,
  useAddUserOverride,
  useAdminFlags,
  useCreateFlag,
  useDeleteFlag,
  useRemoveTeamOverride,
  useRemoveUserOverride,
  useToggleFlag,
} from '@/shared/api/admin-flags';
import { cn } from '@/shared/lib/cn';
import { ReasonDialog } from '@/shared/ui/reason-dialog';

export function FlagsPage() {
  const { data, isLoading } = useAdminFlags();
  const [createOpen, setCreateOpen] = useState(false);
  const [toggleTarget, setToggleTarget] = useState<{ key: string; enabled: boolean } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const toggleMut = useToggleFlag();
  const deleteMut = useDeleteFlag();

  return (
    <AdminLayout>
      <header className="mb-4 flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold">Feature flags</h1>
          <p className="text-sm text-neutral-400">
            Global on/off + per-team / per-user overrides.  Resolution priority: user override → team override → global.
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-700"
        >
          <Plus size={14} /> New flag
        </button>
      </header>

      {isLoading && !data ? (
        <div className="text-sm text-neutral-500">Loading…</div>
      ) : (
        <div className="space-y-3">
          {(data ?? []).map((f) => (
            <FlagCard
              key={f.key}
              flag={f}
              onToggle={() => setToggleTarget({ key: f.key, enabled: !f.enabled })}
              onDelete={() => setDeleteTarget(f.key)}
            />
          ))}
          {data && data.length === 0 ? (
            <p className="text-sm text-neutral-500">No flags yet.  Create one to start gating features.</p>
          ) : null}
        </div>
      )}

      <CreateFlagDialog open={createOpen} onOpenChange={setCreateOpen} />

      {toggleTarget ? (
        <ReasonDialog
          open={true}
          onOpenChange={(o) => !o && setToggleTarget(null)}
          title={`${toggleTarget.enabled ? 'Enable' : 'Disable'} ${toggleTarget.key}?`}
          description="Affects every team / user without an explicit override.  Audit-logged."
          confirmLabel={toggleTarget.enabled ? 'Enable' : 'Disable'}
          tone={toggleTarget.enabled ? 'default' : 'danger'}
          pending={toggleMut.isPending}
          onConfirm={async (reason) => {
            await toggleMut.mutateAsync({ key: toggleTarget.key, enabled: toggleTarget.enabled, reason });
            toast.success(toggleTarget.enabled ? 'Flag enabled' : 'Flag disabled');
            setToggleTarget(null);
          }}
        />
      ) : null}

      {deleteTarget ? (
        <ReasonDialog
          open={true}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title={`Delete ${deleteTarget}?`}
          description="Removes the flag entirely.  Code referencing this key falls back to FALSE.  Audit-logged."
          confirmLabel="Delete"
          tone="danger"
          pending={deleteMut.isPending}
          onConfirm={async (reason) => {
            await deleteMut.mutateAsync({ key: deleteTarget, reason });
            toast.success('Flag deleted');
            setDeleteTarget(null);
          }}
        />
      ) : null}
    </AdminLayout>
  );
}

function FlagCard(props: { flag: AdminFlag; onToggle: () => void; onDelete: () => void }) {
  const f = props.flag;
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <code className="rounded bg-neutral-800 px-2 py-0.5 text-sm font-medium text-orange-400">
              {f.key}
            </code>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ring-1',
                f.enabled
                  ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30'
                  : 'bg-neutral-700/30 text-neutral-500 ring-neutral-700',
              )}
            >
              {f.enabled ? 'enabled globally' : 'disabled globally'}
            </span>
          </div>
          {f.description ? (
            <p className="mt-1 text-sm text-neutral-400">{f.description}</p>
          ) : null}

          <div className="mt-2 flex flex-wrap gap-3 text-xs text-neutral-500">
            <span>Teams override: {f.enabledForTeams.length}</span>
            <span>Users override: {f.enabledForUsers.length}</span>
            <span>Updated {new Date(f.updatedAt).toLocaleDateString()}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={props.onToggle}
            className={cn(
              'rounded-md px-3 py-1.5 text-xs font-medium ring-1 ring-neutral-700',
              f.enabled ? 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700' : 'bg-orange-600 text-white hover:bg-orange-700',
            )}
          >
            {f.enabled ? 'Disable globally' : 'Enable globally'}
          </button>
          <button
            onClick={props.onDelete}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-red-400 hover:bg-red-950 hover:text-red-300"
          >
            <Trash2 size={11} /> Delete
          </button>
        </div>
      </div>

      <div className="mt-3 grid gap-4 border-t border-neutral-800 pt-3 md:grid-cols-2">
        <OverrideList
          flagKey={f.key}
          kind="team"
          ids={f.enabledForTeams}
        />
        <OverrideList
          flagKey={f.key}
          kind="user"
          ids={f.enabledForUsers}
        />
      </div>
    </div>
  );
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Per-flag override editor for one scope (team | user).  Lists the granted ids
 * with a remove control + an "add" input.  Add/remove both go through the
 * ReasonDialog so the audit log captures why.  Operators paste a team/user UUID
 * (a name-search picker is a future nicety).
 */
function OverrideList(props: { flagKey: string; kind: 'team' | 'user'; ids: string[] }) {
  const { flagKey, kind, ids } = props;
  const addTeam = useAddTeamOverride();
  const removeTeam = useRemoveTeamOverride();
  const addUser = useAddUserOverride();
  const removeUser = useRemoveUserOverride();
  const [draft, setDraft] = useState('');
  const [action, setAction] = useState<{ op: 'add' | 'remove'; id: string } | null>(null);

  const label = kind === 'team' ? 'Team' : 'User';
  const draftOk = UUID_RE.test(draft.trim());
  const pending = addTeam.isPending || removeTeam.isPending || addUser.isPending || removeUser.isPending;

  async function run(reason: string) {
    if (!action) return;
    const id = action.id;
    if (kind === 'team') {
      if (action.op === 'add') await addTeam.mutateAsync({ flagKey, teamId: id, reason });
      else await removeTeam.mutateAsync({ flagKey, teamId: id, reason });
    } else {
      if (action.op === 'add') await addUser.mutateAsync({ flagKey, userId: id, reason });
      else await removeUser.mutateAsync({ flagKey, userId: id, reason });
    }
    toast.success(`${label} override ${action.op === 'add' ? 'added' : 'removed'}`);
    if (action.op === 'add') setDraft('');
    setAction(null);
  }

  return (
    <div>
      <div className="mb-1.5 text-xs font-medium uppercase tracking-wider text-neutral-400">
        {label}s override ({ids.length})
      </div>
      <ul className="space-y-1">
        {ids.map((id) => (
          <li key={id} className="flex items-center gap-2">
            <code className="rounded bg-neutral-800 px-1.5 py-0.5 text-xs text-neutral-300">
              {id.slice(0, 8)}…
            </code>
            <button
              onClick={() => setAction({ op: 'remove', id })}
              className="text-neutral-500 hover:text-red-400"
              title={`Remove ${label.toLowerCase()} override`}
            >
              <Trash2 size={12} />
            </button>
          </li>
        ))}
        {ids.length === 0 ? <li className="text-xs text-neutral-600">none</li> : null}
      </ul>
      <div className="mt-2 flex items-center gap-1.5">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={`${label} UUID`}
          className="min-w-0 flex-1 rounded-md bg-neutral-800 px-2 py-1 font-mono text-xs text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
        />
        <button
          disabled={!draftOk || pending}
          onClick={() => setAction({ op: 'add', id: draft.trim() })}
          className="inline-flex items-center gap-1 rounded-md bg-neutral-800 px-2 py-1 text-xs text-neutral-200 ring-1 ring-neutral-700 hover:bg-neutral-700 disabled:opacity-40"
        >
          <Plus size={11} /> Grant
        </button>
      </div>
      {draft && !draftOk ? (
        <div className="mt-1 text-xs text-red-400">Must be a UUID.</div>
      ) : null}

      {action ? (
        <ReasonDialog
          open={true}
          onOpenChange={(o) => !o && setAction(null)}
          title={`${action.op === 'add' ? 'Grant' : 'Remove'} ${label.toLowerCase()} override on ${flagKey}?`}
          description={`${action.op === 'add' ? 'Grants' : 'Removes'} this ${label.toLowerCase()} on the flag's override list.  Audit-logged.`}
          confirmLabel={action.op === 'add' ? 'Grant' : 'Remove'}
          tone={action.op === 'add' ? 'default' : 'danger'}
          pending={pending}
          onConfirm={run}
        />
      ) : null}
    </div>
  );
}

function CreateFlagDialog(props: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const createMut = useCreateFlag();
  const [key, setKey] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [description, setDescription] = useState('');
  const [reason, setReason] = useState('');

  if (!props.open) return null;

  const keyOk = /^[a-z][a-z0-9_]*$/.test(key);
  const reasonOk = reason.trim().length >= 5;
  const canSubmit = keyOk && reasonOk && !createMut.isPending;

  async function submit() {
    if (!canSubmit) return;
    const input: CreateFlagInput = { key, enabled, reason: reason.trim() };
    if (description.trim()) input.description = description.trim();
    try {
      await createMut.mutateAsync(input);
      toast.success(`Flag ${key} created`);
      props.onOpenChange(false);
      setKey('');
      setEnabled(false);
      setDescription('');
      setReason('');
    } catch {
      /* toast'ed */
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl bg-neutral-900 p-6 shadow-2xl ring-1 ring-neutral-800">
        <h2 className="mb-1 text-base font-semibold text-neutral-100">New feature flag</h2>
        <p className="mb-4 text-sm text-neutral-400">
          Create a flag, then reference it in code via{' '}
          <code className="text-orange-400">FeatureFlagsService.isEnabled(key, ctx)</code>.
        </p>

        <div className="mb-3">
          <label className="mb-1 block text-xs uppercase tracking-wider text-neutral-400">
            Key (lowercase letters/digits/underscores)
          </label>
          <input
            value={key}
            onChange={(e) => setKey(e.target.value.toLowerCase())}
            placeholder="ai_smart_create"
            className="w-full rounded-md bg-neutral-800 px-3 py-2 font-mono text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          />
          {key && !keyOk ? (
            <div className="mt-1 text-xs text-red-400">Invalid: lowercase letters/digits/underscores only.</div>
          ) : null}
        </div>

        <div className="mb-3">
          <label className="mb-1 block text-xs uppercase tracking-wider text-neutral-400">Description (optional)</label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this flag controls"
            className="w-full rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          />
        </div>

        <label className="mb-3 flex items-center gap-2 text-sm text-neutral-300">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-600 bg-neutral-800"
          />
          Enable globally on create
        </label>

        <div className="mb-4">
          <label className="mb-1 block text-xs uppercase tracking-wider text-neutral-400">
            Reason (≥5 chars; recorded in audit log)
          </label>
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full resize-none rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          />
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-neutral-800 pt-4">
          <button
            onClick={() => props.onOpenChange(false)}
            className="rounded-md px-3 py-1.5 text-sm text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200"
          >
            Cancel
          </button>
          <button
            disabled={!canSubmit}
            onClick={() => void submit()}
            className="rounded-md bg-orange-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
          >
            {createMut.isPending ? 'Creating…' : 'Create flag'}
          </button>
        </div>
      </div>
    </div>
  );
}
